/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC } from "react"
import { Row, Col } from "@duxton-web/ui"
import { useDispatch, useSelector, shallowEqual } from "react-redux"
import classnames from "classnames"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { Item } from "types/models/menu"
import { SellingTimeTag } from "components/tags"
import { getSellingTimeByID } from "utils/selling_time_helper"
import { isItemSelectedV3 } from "utils/bulk_selection_helpers"
import { useSellingTimes } from "hooks/useSellingTimes"
import {
  isItemUnavailable,
  MenuItemRowCheckbox,
  MenuItemRowColumns,
} from "./shared/MenuItemRowShared"
import styles from "./MenuItemRow.modules.css"

interface IProps {
  item: Item
  showSelection?: boolean
  className?: string
}

export const MenuItemRowV3: FC<IProps> = ({
  item,
  showSelection,
  className,
}: IProps) => {
  const dispatch = useDispatch()

  const {
    enableAlcoholSetting,
    bulkSelectionsV3,
    selectedSubDepartment,
  } = useSelector(
    (state: AppState) => ({
      enableAlcoholSetting: state.menu.enableAlcoholSetting,
      bulkSelectionsV3: state.bulkSelectionsV3,
      selectedSubDepartment: state.menu.overview.subDepartment,
    }),
    shallowEqual,
  )

  const { sellingTimes } = useSellingTimes()

  // V3 mode: Calculate checked state using BulkSelectionsMetadata
  const selected = React.useMemo(() => {
    return isItemSelectedV3(
      bulkSelectionsV3,
      item?.itemID ?? "",
      item?.subDepartmentID ?? "",
      item?.departmentID ?? "",
    )
  }, [item, bulkSelectionsV3])

  const handleCheck = (value: boolean) => {
    dispatch(actionCreator.bulkSelectionsV3.multiSelectItem({ item, value }))
  }

  const isUnavailable = isItemUnavailable(item, enableAlcoholSetting)

  return (
    <Row
      wrap={false}
      gutter={16}
      className={classnames(styles.rowStyle, className)}
    >
      <MenuItemRowColumns item={item} isUnavailable={isUnavailable} />
      {item.sellingTimeID !== selectedSubDepartment?.sellingTimeID && (
        <Col>
          <SellingTimeTag
            sellingTime={getSellingTimeByID(item.sellingTimeID, sellingTimes)}
          />
        </Col>
      )}

      <MenuItemRowCheckbox
        showSelection={showSelection}
        selected={selected}
        onCheck={handleCheck}
      />
    </Row>
  )
}
